import React from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

/**
 * Reference Code Mapping for Cut & Fills columns
 * Contains reference codes and descriptions for Site Clearance and Cut & Fill operations
 */

export interface ReferenceCodeMapping {
  code?: string;
}

/**
 * Site Clearance Reference Codes
 */
export const SITE_CLEARANCE_REFERENCES: Record<string, ReferenceCodeMapping> = {
  vegStrip: { code: "ALP033b" },
  topsoilStrip: { code: "GWALP001" },
  topsoilDisposalOnsite: { code: 'GWALP035' },
  topsoilDisposalOffsite: { code: 'AMAS005' },
};

/**
 * Cut & Fill Reference Codes
 */
export const CUT_FILL_REFERENCES: Record<string, ReferenceCodeMapping> = {
  cutFounds: { code: "GWALP002" },
  fillFounds: { code: "GWALP003" },
  cutPaths: { code: "GWALP002" },
  fillPaths: { code: "GWALP003" },
  cutGarden: { code: "GWALP002" },
  fillGarden: { code: "GWALP003" },
  cutDrives: { code: "GWALP002" },
  fillDrives: { code: "GWALP003" },
  fillToMakeUp6F2: { code: "GWALP004" },
};

/**
 * All Reference Codes Combined
 */
export const ALL_REFERENCES: Record<string, ReferenceCodeMapping> = {
  ...SITE_CLEARANCE_REFERENCES,
  ...CUT_FILL_REFERENCES,
};

/**
 * Helper function to create a column title with reference code and tooltip
 * @param title - The column title text
 * @param referenceKey - The key to lookup in the reference mapping
 * @param referenceMap - The reference mapping object (defaults to ALL_REFERENCES)
 * @returns React element with title, info icon, and reference code
 */
export const createColumnTitleWithReference = (
  title: string,
  referenceKey: string,
  referenceMap: Record<string, ReferenceCodeMapping> = ALL_REFERENCES,
): React.ReactNode => {
  const reference = referenceMap[referenceKey];

  if (!reference) {
    // If no reference found, return plain title
    return title;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
      }}
    >
      <span>{title}</span>
      {/* Commented out evaluate codes and description */}
      {/* <Tooltip title={reference.description}>
        <InfoCircleOutlined style={{ fontSize: "12px", color: "#1890ff" }} />
      </Tooltip>
      {reference.code && (
        <div
          style={{
            fontSize: "11px",
            color: "#8c8c8c",
            marginTop: "2px",
          }}
        >
          ({reference.code})
        </div>
      )} */}
    </div>
  );
};

/**
 * Get reference code for a specific field
 * @param referenceKey - The key to lookup
 * @returns The reference code or undefined
 */
export const getReferenceCode = (referenceKey: string): string | undefined => {
  return ALL_REFERENCES[referenceKey]?.code;
};

/**
 * Get description for a specific field
 * @param referenceKey - The key to lookup
 * @returns The description or undefined
 */
export const getReferenceDescription = (
  referenceKey: string,
): string | undefined => {
  return ALL_REFERENCES[referenceKey]?.description;
};

/**
 * Get full reference information for a specific field
 * @param referenceKey - The key to lookup
 * @returns The reference object or undefined
 */
export const getReference = (
  referenceKey: string,
): ReferenceCodeMapping | undefined => {
  return ALL_REFERENCES[referenceKey];
};

/**
 * Check if a field has a reference code
 * @param referenceKey - The key to lookup
 * @returns True if the field has a reference code
 */
export const hasReferenceCode = (referenceKey: string): boolean => {
  return !!ALL_REFERENCES[referenceKey]?.code;
};

/**
 * Get all reference codes for Site Clearance
 * @returns Array of reference keys and codes
 */
export const getSiteClearanceReferenceCodes = (): Array<{
  key: string;
  code?: string;
  description: string;
}> => {
  return Object.entries(SITE_CLEARANCE_REFERENCES).map(([key, value]) => ({
    key,
    code: value.code,
    description: value.description,
  }));
};

/**
 * Get all reference codes for Cut & Fill
 * @returns Array of reference keys and codes
 */
export const getCutFillReferenceCodes = (): Array<{
  key: string;
  code?: string;
  description: string;
}> => {
  return Object.entries(CUT_FILL_REFERENCES).map(([key, value]) => ({
    key,
    code: value.code,
    description: value.description,
  }));
};

/**
 * Export reference codes as JSON for documentation or API usage
 * @returns JSON object with all reference codes
 */
export const exportReferencesAsJSON = () => {
  return {
    siteClearance: SITE_CLEARANCE_REFERENCES,
    cutFill: CUT_FILL_REFERENCES,
    all: ALL_REFERENCES,
  };
};

/**
 * Column width recommendations based on whether column has reference code
 */
export const COLUMN_WIDTHS = {
  withReferenceCode: 150,
  withoutReferenceCode: 120,
  withLongReferenceCode: 180,
};

/**
 * Get recommended column width for a field
 * @param referenceKey - The key to lookup
 * @returns Recommended width in pixels
 */
export const getRecommendedColumnWidth = (referenceKey: string): number => {
  const reference = ALL_REFERENCES[referenceKey];
  if (!reference) return COLUMN_WIDTHS.withoutReferenceCode;
  if (reference.code) return COLUMN_WIDTHS.withReferenceCode;
  return COLUMN_WIDTHS.withoutReferenceCode;
};
